import React, { useEffect, useRef, useMemo } from "react";
import styles from "./styles.module.scss";
import BarChart from "../BarChart";
import GroupedBarsChart from "../GroupedBarsChart";
import PieChart from "../PieChart";
import LineChart from "../LineChart";
import StackedBarsChart from "../StackedBarsChart";  
import HeatmapChart from "../HeatmapChart";
import MosaicChart from "../MosaicChart";
import MultipleLineChart from "../MultipleLineChart";
import { getImagePath } from "../../getImagePath";
import { ALL_CHART_TYPES } from "../../CustomDataInputPage/DATA_TYPES";
import ChartInfo from "../ChartInfo";
import STATISTIC_TYPES, {
    FIND_STATISTIC_TYPE_BY_VALUE,
    ALL_STATISTICS_TYPES,
} from "../STATISTIC_TYPES";

function DataImageGenerator({ data, chartType, statisticsType, onLoaded }) {
    const containerRef = useRef(null);

    const chartTitle = useMemo(() => {
        if (!data) {
            return "";
        }
        if (!data.title2 && !data.title3) {
            return `<span>${data.title}</span>`;
        }
        if (!data.title3) {
            return `<span>${data.title}</span> <small>（依 ${data.title2} 分類）</small>`;
        }
        return `<span>${data.title}</span> <small>（依 ${data.title2} 及 ${data.title3} 分類）</small>`;
    }, [data]);

    useEffect(() => {
        if (!data || !chartType || !statisticsType) {
            return;
        }

        setTimeout(() => {
            onLoaded(containerRef.current);
        }, 100);
    }, [data, chartType, statisticsType, onLoaded]);

    const getChartNote = useMemo(() => {

            // 堆疊長條圖需要顯示 title2
            if (chartType === ALL_CHART_TYPES.STACK_BAR) {
                // 三層圖表才需要顯示註解
                if (data.title3) {
                    return `X軸為 ${data.title2} 的 ${
                        FIND_STATISTIC_TYPE_BY_VALUE(statisticsType)
                            .label
                    }\nY軸為 ${
                        data.title3
                    } 的選項\n將游標移到圖表上，可查看詳細數據說明。`;
                }
                return `X軸為 ${data.title2} 的 ${
                    FIND_STATISTIC_TYPE_BY_VALUE(statisticsType).label
                }\nY軸為 ${
                    data.title
                } 的選項\n將游標移到圖表上，可查看詳細數據說明。`;
            }

            // 熱力圖需要顯示 title2 和 title3
            if (chartType === ALL_CHART_TYPES.HEATMAP) {
                if (data.title3) {
                    return `X軸為 ${data.title3} 的選項\nY軸為 ${
                        data.title2
                    } 的選項\n顏色深度為 ${data.title} 的 ${
                        FIND_STATISTIC_TYPE_BY_VALUE(statisticsType)
                            .label
                    }\n將游標移到圖表上，可查看詳細數據說明。`;
                }
                return `X軸為 ${data.title2} 的選項\nY軸為 ${
                    data.title
                } 的選項\n顏色深度為 ${data.title} 的 ${
                    FIND_STATISTIC_TYPE_BY_VALUE(statisticsType).label
                }\n將游標移到圖表上，可查看詳細數據說明。`;
            }

            // 馬賽克圖需要顯示 title2
            if (chartType === ALL_CHART_TYPES.MOSAIC) {
                return `X軸為${data.title}的選項\nY軸為${data.title2}的${
                    FIND_STATISTIC_TYPE_BY_VALUE(statisticsType).label
                }\n將游標移到圖表上，可查看詳細數據說明。`;
            }

            //長條圖
            if (chartType === ALL_CHART_TYPES.BAR) {
                if (data.title2) {
                    return `X軸為 ${data.title} 的 ${
                        FIND_STATISTIC_TYPE_BY_VALUE(statisticsType)
                            .label
                    }\nY軸為 ${data.title2} 的選項`;
                }
                return `X軸為 ${data.title} 的 ${
                    FIND_STATISTIC_TYPE_BY_VALUE(statisticsType).label
                }\nY軸為 ${data.title} 的選項`;
            }

            //分組長條圖
            if (chartType === ALL_CHART_TYPES.GROUPED_BAR) {
                return `X軸為 ${data.title} 的 ${
                    FIND_STATISTIC_TYPE_BY_VALUE(statisticsType).label
                }\nY軸為 ${data.title2} 的選項`;
            }

            if (chartType === ALL_CHART_TYPES.LINE) {
                // 如果只有一層
                if (!data.title2 && !data.title3) {
                    return `X軸為 ${data.title} 的選項\nY軸為 ${
                        data.title
                    } 的 ${
                        FIND_STATISTIC_TYPE_BY_VALUE(statisticsType)
                            .label
                    }`;
                }
                return `X軸為 ${data.title2} 的選項\nY軸為 ${data.title} 的 ${
                    FIND_STATISTIC_TYPE_BY_VALUE(statisticsType).label
                }`;
            }

            // 圓餅圖和單層圖表不需要註解
            return null;
        }, [data, statisticsType, chartType]);

    return (
        <div
            id="data-image-generator"
            className={styles.dataImageGenerator}
            ref={containerRef}
        >
            <div className={styles.header}>
                <img src={getImagePath("ncdr-logo.png")} alt="ncdr-logo" />
            </div>
            <div className={styles.container}>
                <h2>
                    <h3 dangerouslySetInnerHTML={{ __html: chartTitle }} />
                    <small>
                        {statisticsType == 1
                            ? "統計類型：百分比"
                            : "統計類型：實際數量"}
                    </small>
                </h2>

                <div className={styles.chartContainer}>
                    {chartType === ALL_CHART_TYPES.BAR && (
                        <BarChart
                            data={data.values}
                            statisticsType={statisticsType}
                            chartTitle={data.title}
                            chart2Title={data.title2}
                            chart3Title={data.title3}
                            chartNote={getChartNote}Add commentMore actions
                        />
                    )}
                    {chartType === ALL_CHART_TYPES.GROUPED_BAR && (
                        <GroupedBarsChart
                            data={data.values}
                            statisticsType={statisticsType}
                            chartTitle={data.title}
                            chart2Title={data.title2}
                            chart3Title={data.title3}
                            chartNote={getChartNote}
                        />
                    )}
                    {chartType === ALL_CHART_TYPES.PIE && (
                        <PieChart
                            data={data.values}
                            statisticsType={statisticsType}
                            chartTitle={data.title}
                            chart2Title={data.title2}
                            chart3Title={data.title3}
                        />
                    )}
                    {chartType === ALL_CHART_TYPES.LINE && (
                        <LineChart
                            data={data.values}
                            statisticsType={statisticsType}
                            chartTitle={data.title}
                            chart2Title={data.title2}
                            chart3Title={data.title3}
                            chartNote={getChartNote}
                        />
                    )}
                    {chartType === ALL_CHART_TYPES.STACK_BAR && (
                        <StackedBarsChart
                            data={data.values}
                            statisticsType={statisticsType}
                            chartTitle={data.title}
                            chart2Title={data.title2}
                            chart3Title={data.title3}
                            chartNote={getChartNote}
                        />
                    )}
                    {chartType === ALL_CHART_TYPES.HEATMAP && (
                        <HeatmapChart
                            data={data.values}
                            statisticsType={statisticsType}
                            chartTitle={data.title}
                            chart2Title={data.title2}
                            chart3Title={data.title3}
                            chartNote={getChartNote}
                        />
                    )}
                    {chartType === ALL_CHART_TYPES.MOSAIC && (
                        <MosaicChart
                            data={data.values}
                            statisticsType={statisticsType}
                            chartTitle={data.title}
                            chart2Title={data.title2}
                            chart3Title={data.title3}                     
                            chartNote={getChartNote}
                            />
                    )}
                    {chartType === ALL_CHART_TYPES.MULTIPLE_LINE && (
                        <MultipleLineChart
                            data={data.values}
                            statisticsType={statisticsType}
                            chartTitle={data.title}
                            chart2Title={data.title2}
                            chart3Title={data.title3}
                            chartNote={getChartNote}
                        />
                    )}
                    {/* {chartType === ALL_CHART_TYPES.BAR && (
                        <BarChart
                            data={data.values}
                            chartTitle={data.title}
                            chart2Title={data.title2}
                            chart3Title={data.title3}
                            statisticsType={statisticsType}
                        />
                    )} */}
                    {chartType === ALL_CHART_TYPES.PIE && (
                        <PieChart
                            data={data.values}
                            chartTitle={data.title}
                            chart2Title={data.title2}
                            chart3Title={data.title3}
                            statisticsType={statisticsType}
                            legendOnTop={true}
                        />
                    )}
                    {chartType === ALL_CHART_TYPES.LINE && (
                        <LineChart
                            data={data.values}
                            chartTitle={data.title}
                            chart2Title={data.title2}
                            chart3Title={data.title3}
                            statisticsType={statisticsType}
                            legendOnTop={true}
                        />
                    )}
                </div>
                <ChartInfo
                    data={data}
                    note="圖片來源：國家災害防救科技中心減災動資料 0918 池上地震花東地區—客製化圖表專區"
                />
            </div>
        </div>
    );
}

export default DataImageGenerator;